import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["activeTrials", "expiredTrials", "isMultiTrial", "hideBackground", "upgradeData", "upgradeData", "onSuccess", "shouldHideSurvey"];
import Raven from 'raven-js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { CMS_HUB_ENTERPRISE, MARKETING_PLUS_ENTERPRISE_BUNDLE } from 'self-service-api/constants/ApiNames';
import { apiNameToTrialIds, trialIdsWithTrialGuide, upgradeProductToTrialIds } from 'self-service-api/constants/Trials';
// @ts-expect-error dependency missing types
import UpgradeProductToApiNameMap from 'self-service-api/constants/UpgradeProductToApiNameMap';
import { getUpgradeDataTrackingProperties } from 'ui-addon-upgrades/ums/utils';
import ActivationDuplicateErrorModal from '../../common/components/activation/ActivationDuplicateErrorModal';
import ActivationErrorModal from '../../common/components/activation/ActivationErrorModal';
import { track } from '../../common/eventTracking/tracker';
import { navigateTo } from '../../common/navigation/url';
import { TRIAL } from '../../purchaseMotions/PurchaseMotionTypes';
import { getCampaignsUrl, getTrialGuideUrl, getTrialGuideUrlFromApiName, getWebsitesUrl, refresh } from '../../utils/links';
import { trackFailedApiNameMap, trackPurchaseMotionFailure, trackTrialActivation } from '../../utils/metricUtils';
import TrialActivationSuccessContainer from './TrialActivationSuccessContainer';
import { activateTrial } from './activateTrial';
import { jsx as _jsx } from "react/jsx-runtime";
const statuses = {
  LOADING: 'loading',
  ERROR: 'error',
  SUCCESS: 'success'
};
const HIDE_SUBTITLE_SCREENS = ['products-plans', 'service-hub-paywall'];
const TrialActivationModalContainer = props => {
  const [trialActivationRequestStatus, setTrialActivationRequestStatus] = useState(statuses.LOADING);
  const [showTrialLoadingState, setShowTrialLoadingState] = useState(!!props.shouldHideSurvey);
  const [error, setError] = useState(null);
  const isMounted = useRef(false);
  const {
      activeTrials,
      expiredTrials,
      isMultiTrial,
      hideBackground = false,
      upgradeData,
      upgradeData: {
        app,
        screen,
        uniqueId,
        upgradeProduct
      },
      onSuccess,
      shouldHideSurvey
    } = props,
    successModalPassthroughProps = _objectWithoutPropertiesLoose(props, _excluded);
  const apiName = upgradeData.apiName || UpgradeProductToApiNameMap[upgradeData.upgradeProduct];
  const showSubtitle = !HIDE_SUBTITLE_SCREENS.includes(screen);
  const defaultOnSuccess = useCallback(() => {
    if (onSuccess) {
      onSuccess();
    } else {
      var _window$opener;
      const getTrialId = () => {
        if (apiNameToTrialIds[apiName]) {
          return apiNameToTrialIds[apiName];
        } else {
          trackFailedApiNameMap('getTrialId', apiName || 'no api name', upgradeProduct);
          Raven.captureException(new Error(`failed to get trial id from apiName ${apiName} in getTrialId`));
          return upgradeProductToTrialIds[upgradeProduct];
        }
      };
      const trialId = getTrialId();
      if (apiName === MARKETING_PLUS_ENTERPRISE_BUNDLE) {
        navigateTo(getCampaignsUrl());
      } else if ([CMS_HUB_ENTERPRISE].includes(apiName)) {
        navigateTo(getWebsitesUrl());
      } else if (typeof window !== 'undefined' && (_window$opener = window.opener) !== null && _window$opener !== void 0 && _window$opener.location.href) {
        navigateTo(window.opener.location.href);
      } else if (trialId && trialIdsWithTrialGuide.includes(trialId)) {
        const source = `${app}-${screen}-${uniqueId}`;
        navigateTo(apiName ? getTrialGuideUrlFromApiName(apiName, source) : getTrialGuideUrl(upgradeProduct, source));
      } else {
        refresh();
      }
    }
  }, [onSuccess, app, screen, uniqueId, upgradeProduct, apiName]);
  useEffect(() => {
    isMounted.current = true;
    track('trialModalInteraction', Object.assign({
      action: 'opened modal container'
    }, getUpgradeDataTrackingProperties(upgradeData), {
      trialId: upgradeData.trialId,
      duration: upgradeData.duration
    }));
    activateTrial({
      upgradeData,
      isMultiTrial,
      activeTrials,
      expiredTrials
    }).then(() => {
      if (isMounted.current) {
        setTrialActivationRequestStatus(statuses.SUCCESS);
        setShowTrialLoadingState(false);
        trackTrialActivation(upgradeData.app, upgradeData.upgradeProduct);
      }
    }).catch(err => {
      if (isMounted.current) {
        trackPurchaseMotionFailure({
          uniqueId: upgradeData.uniqueId,
          apiName: upgradeData.apiName || UpgradeProductToApiNameMap[upgradeData.upgradeProduct],
          purchaseMotion: TRIAL
        });
        setTrialActivationRequestStatus(statuses.ERROR);
        setError(err);
      }
    });
    return () => {
      isMounted.current = false;
    };
  }, [activeTrials, expiredTrials, isMultiTrial, upgradeData]);
  switch (trialActivationRequestStatus) {
    case statuses.LOADING:
      return null;
    case statuses.ERROR:
      track('trialModalInteraction', Object.assign({
        action: 'opening activation error modal'
      }, getUpgradeDataTrackingProperties(upgradeData), {
        trialId: upgradeData.trialId,
        duration: upgradeData.duration
      }));
      if (error && error.responseJSON) {
        const activateError = error.responseJSON;
        if (activateError.message && activateError.message.match(/^Cannot start duplicate trial/)) {
          return /*#__PURE__*/_jsx(ActivationDuplicateErrorModal, {
            onSuccess: defaultOnSuccess,
            upgradeData: upgradeData
          });
        }
      }
      return /*#__PURE__*/_jsx(ActivationErrorModal, {});
    case statuses.SUCCESS:
      track('trialModalInteraction', Object.assign({
        action: 'opening activation success modal'
      }, getUpgradeDataTrackingProperties(upgradeData), {
        trialId: upgradeData.trialId,
        duration: upgradeData.duration
      }));
      return /*#__PURE__*/_jsx(TrialActivationSuccessContainer, Object.assign({
        onSuccess: defaultOnSuccess,
        activeTrials: activeTrials,
        expiredTrials: expiredTrials,
        isMultiTrial: isMultiTrial,
        hideBackground: hideBackground,
        upgradeData: upgradeData,
        showTrialLoadingState: showTrialLoadingState,
        showSubtitle: showSubtitle,
        shouldHideSurvey: shouldHideSurvey
      }, successModalPassthroughProps));
    default:
      return null;
  }
};
export default TrialActivationModalContainer;